import { useState, useEffect, useRef } from "react";
import { gsap } from "gsap";

const translations = {
  en: {
    title: "Users",
    userList: "User List",
    sortByDate: "Sort by Account Creation Date:",
    newestFirst: "Newest First",
    oldestFirst: "Oldest First",
    filterByType: "Filter by Account Type:",
    allTypes: "All Types",
    admin: "Admin",
    recruiter: "Recruiter",
    candidate: "Candidate",
    email: "Email",
    accountType: "Account Type",
    reports: "Reports",
    dateGenerated: "Date Generated",
    actions: "Actions",
    loading: "Loading users...",
    noUsers: "No Users Yet",
    noReports: "No Reports yet",
    oneReport: "1 Report",
    multipleReports: "Reports",
    viewReports: "View Reports"
  },
  nl: {
    title: "Gebruikers",
    userList: "Gebruikerslijst",
    sortByDate: "Sorteer op aanmaakdatum:",
    newestFirst: "Nieuwste eerst",
    oldestFirst: "Oudste eerst",
    filterByType: "Filter op accounttype:",
    allTypes: "Alle types",
    admin: "Beheerder",
    recruiter: "Recruiter",
    candidate: "Kandidaat",
    email: "E-mail",
    accountType: "Accounttype",
    reports: "Rapporten",
    dateGenerated: "Datum gegenereerd",
    actions: "Acties",
    loading: "Gebruikers laden...",
    noUsers: "Nog geen gebruikers",
    noReports: "Nog geen rapporten",
    oneReport: "1 Rapport",
    multipleReports: "Rapporten",
    viewReports: "Bekijk rapporten"
  }
};

function Users({
  users,
  setUsers,
  loading,
  setLoading,
  error,
  setError,
  handleUserSelect,
  isDutch
}) {
  const t = translations[isDutch ? 'nl' : 'en'];
  const [selectedAccountType, setSelectedAccountType] = useState("all");
  const [filterByDate, setFilterByDate] = useState("newestFirst");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isAccountTypeDropdownOpen, setIsAccountTypeDropdownOpen] =
    useState(false);
  const accountTypeDropdownRef = useRef(null);

  const setGSAPProperties = (element, props) => {
    if (props.force3D) {
      element.style.transform = 'translate3d(0, 0, 0)';
    }
    if (props.willChange) {
      element.style.willChange = props.willChange;
    }
  };

  const resetGSAPProperties = (element) => {
    element.style.willChange = 'auto';
  };

  // Direct fadeUp animation
  const initFadeUp = () => {
    const fadeUpElements = document.querySelectorAll(".fadeUp");

    if (fadeUpElements.length === 0) {
      setTimeout(initFadeUp, 100);
      return;
    }

    // Set CSS properties directly for better performance
    fadeUpElements.forEach(element => {
      setGSAPProperties(element, { force3D: true, willChange: "transform, opacity" });
    });

    const animationDelay = 0; // Base delay in milliseconds

    gsap.set(fadeUpElements, { 
      opacity: 0, 
      y: 20
    });
    
    gsap.to(fadeUpElements, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      delay: 0.2 + (animationDelay / 1000), // Add navbar delay to base delay
      stagger: 0.1,
      ease: "power2.out",
      onComplete: () => {
        // Reset CSS properties after animation
        fadeUpElements.forEach(element => {
          resetGSAPProperties(element);
        });
      }
    });
  };

  useEffect(() => {
    if (users) {
      initFadeUp();
    }
  }, [users, selectedAccountType, filterByDate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        accountTypeDropdownRef.current &&
        !accountTypeDropdownRef.current.contains(event.target)
      ) {
        setIsAccountTypeDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [accountTypeDropdownRef]);

  return (
    <div className="m-0 text-left">
      <h1 className="text-5xl font-bold mb-4 text-primary">{t.title}</h1>
      <main className="flex flex-col gap-4">
        
        <div className="flex justify-between gap-2 items-center mt-8">
        <h2 className="text-2xl font-bold">{t.userList}</h2>
        <div className="flex gap-4">
          <div className="flex items-center gap-4">
            <span>{t.sortByDate}</span>
            <div ref={dropdownRef} className="relative">
              <div
                className="border p-2 rounded-md cursor-pointer flex items-center gap-2 hover:bg-gray-50"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span>
                  {filterByDate === "newestFirst"
                    ? t.newestFirst
                    : t.oldestFirst}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border rounded-md shadow-lg z-10">
                  <div
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                    onClick={() => {
                      setFilterByDate("newestFirst");
                      setIsDropdownOpen(false);
                    }}
                  >
                    {t.newestFirst}
                  </div>
                  <div
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                    onClick={() => {
                      setFilterByDate("oldestFirst");
                      setIsDropdownOpen(false);
                    }}
                  >
                    {t.oldestFirst}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span>{t.filterByType}</span>
            <div ref={accountTypeDropdownRef} className="relative">
              <div
                className="border p-2 rounded-md cursor-pointer flex items-center gap-2 hover:bg-gray-50"
                onClick={() =>
                  setIsAccountTypeDropdownOpen(!isAccountTypeDropdownOpen)
                }
              >
                <span>
                  {selectedAccountType === "all"
                    ? t.allTypes
                    : selectedAccountType === "2"
                    ? t.admin
                    : selectedAccountType === "3"
                    ? t.recruiter
                    : selectedAccountType === "4"
                    ? t.candidate
                    : t.allTypes}
                </span>
                <svg
                  className={`w-4 h-4 transition-transform ${
                    isAccountTypeDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {isAccountTypeDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white border rounded-md shadow-lg z-10">
                  <div
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                    onClick={() => {
                      setSelectedAccountType("all");
                      setIsAccountTypeDropdownOpen(false);
                    }}
                  >
                    {t.allTypes}
                  </div>
                  <div
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                    onClick={() => {
                      setSelectedAccountType("2");
                      setIsAccountTypeDropdownOpen(false);
                    }}
                  >
                    {t.admin}
                  </div>
                  <div
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                    onClick={() => {
                      setSelectedAccountType("3");
                      setIsAccountTypeDropdownOpen(false);
                    }}
                  >
                    {t.recruiter}
                  </div>
                  <div
                    className="p-2 hover:bg-gray-50 cursor-pointer rounded-md"
                    onClick={() => {
                      setSelectedAccountType("4");
                      setIsAccountTypeDropdownOpen(false);
                    }}
                  >
                    {t.candidate}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              <th className="border border-gray-300 p-2">{t.email}</th>
              <th className="border border-gray-300 p-2">{t.accountType}</th>
              <th className="border border-gray-300 p-2">{t.reports}</th>
              <th className="border border-gray-300 p-2">{t.dateGenerated}</th>
              <th className="border border-gray-300 p-2">{t.actions}</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="4" className="border border-gray-300 p-2">
                  {t.loading}
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan="4" className="border border-gray-300 p-2">
                  {t.noUsers}
                </td>
              </tr>
            )}
            {users &&
              users
                .sort((a, b) => {
                  const dateA = new Date(a.datetimeInserted);
                  const dateB = new Date(b.datetimeInserted);
                  if (filterByDate === "newestFirst") {
                    return dateB - dateA; 
                  } else {
                    return dateA - dateB; 
                  }
                })
                .filter((user) => {
                  if (selectedAccountType === "all") {
                    return true; 
                  } else if (selectedAccountType === "2") {
                    return user.accountType === 1 || user.accountType === 2; 
                  } else {
                    return user.accountType.toString() === selectedAccountType;
                  }
                })
                .map((user) => (
                  <tr key={user.id} className="fadeUp">
                    <td className="border border-gray-300 p-2">{user.email}</td>
                    <td className="border border-gray-300 p-2">
                      {user.accountType === 1 || user.accountType === 2
                        ? t.admin
                        : user.accountType === 3
                        ? t.recruiter
                        : user.accountType === 4
                        ? t.candidate
                        : ""}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {user.reportCount === 0
                        ? t.noReports
                        : user.reportCount === 1
                        ? t.oneReport
                        : `${user.reportCount} ${t.multipleReports}`}
                    </td>
                    <td className="border border-gray-300 p-2">
                      {new Date(user.datetimeInserted).toLocaleDateString()}
                    </td>
                    <td className="border border-gray-300 p-2">
                      <div className="flex gap-2">
                        <button
                          className="border bg-default text-white px-4 py-2 rounded-md"
                          onClick={() => handleUserSelect(user.id)}
                        >
                          {t.viewReports}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </main>
    </div>
  );
}

export default Users;
